import axios from 'axios';
export { default as axios } from 'axios';

// src/client.ts
var getDefaultBaseURL = () => {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return "http://localhost:3001/api/v1";
};
var DEFAULT_CONFIG = {
  baseURL: getDefaultBaseURL(),
  timeout: 3e4
};
var ApiClient = class {
  constructor(config = {}) {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        "Content-Type": "application/json"
      }
    });
    this.setupInterceptors();
  }
  // ============================================
  // INTERCEPTORS
  // ============================================
  setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = this.config.getAccessToken?.();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (typeof FormData !== "undefined" && config.data instanceof FormData && config.headers) {
          config.headers["Content-Type"] = void 0;
          config.headers["content-type"] = void 0;
          if (config.headers.post) {
            config.headers.post["Content-Type"] = void 0;
            config.headers.post["content-type"] = void 0;
          }
          if (config.headers.common) {
            config.headers.common["Content-Type"] = void 0;
            config.headers.common["content-type"] = void 0;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.client(originalRequest));
              });
            });
          }
          originalRequest._retry = true;
          this.isRefreshing = true;
          try {
            const refreshToken = this.config.getRefreshToken?.();
            if (refreshToken) {
              const response = await this.client.post("/auth/refresh", {
                refreshToken
              });
              const responseData = response.data.data || response.data;
              const { accessToken, refreshToken: newRefreshToken } = responseData;
              this.config.onAccessTokenRefreshed?.(accessToken, newRefreshToken);
              this.refreshSubscribers.forEach((callback) => callback(accessToken));
              this.refreshSubscribers = [];
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            this.config.onUnauthorized?.();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }
        const apiError = this.transformError(error);
        this.config.onError?.(apiError);
        return Promise.reject(apiError);
      }
    );
  }
  // ============================================
  // ERROR HANDLING
  // ============================================
  transformError(error) {
    if (error.response) {
      const data = error.response.data;
      const errorObj = data?.error;
      return {
        status: error.response.status,
        message: errorObj?.message || data?.message || "Bir hata olu\u015Ftu",
        code: errorObj?.code || data?.code,
        errors: errorObj?.details || data?.errors
      };
    }
    if (error.request) {
      return {
        status: 0,
        message: "Sunucuya ba\u011Flan\u0131lamad\u0131",
        code: "NETWORK_ERROR"
      };
    }
    return {
      status: 0,
      message: error.message || "Bilinmeyen bir hata olu\u015Ftu",
      code: "UNKNOWN_ERROR"
    };
  }
  // ============================================
  // HTTP METHODS
  // ============================================
  async get(url, config) {
    const response = await this.client.get(url, config);
    return response.data;
  }
  async post(url, data, config) {
    const response = await this.client.post(url, data, config);
    return response.data;
  }
  async put(url, data, config) {
    const response = await this.client.put(url, data, config);
    return response.data;
  }
  async patch(url, data, config) {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }
  async delete(url, config) {
    const response = await this.client.delete(url, config);
    return response.data;
  }
  // ============================================
  // FILE UPLOAD
  // ============================================
  async upload(url, file, fieldName = "file", onProgress, config) {
    const formData = new FormData();
    formData.append(fieldName, file);
    const response = await this.client.post(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": void 0
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percent = Math.round(progressEvent.loaded * 100 / progressEvent.total);
          onProgress(percent);
        }
      }
    });
    return response.data;
  }
  // ============================================
  // AXIOS INSTANCE ACCESS
  // ============================================
  getAxiosInstance() {
    return this.client;
  }
  setBaseURL(baseURL) {
    this.client.defaults.baseURL = baseURL;
    this.config.baseURL = baseURL;
  }
};
var apiClientInstance = null;
function createApiClient(config = {}) {
  apiClientInstance = new ApiClient(config);
  return apiClientInstance;
}
function getApiClient() {
  if (!apiClientInstance) {
    apiClientInstance = new ApiClient();
  }
  return apiClientInstance;
}

// src/endpoints/auth.ts
var authApi = {
  // ============================================
  // REGISTER
  // ============================================
  /**
   * Register as seafarer
   */
  registerSeafarer: (data) => {
    return getApiClient().post("/auth/register/seafarer", data);
  },
  /**
   * Register as company
   */
  registerCompany: (data) => {
    return getApiClient().post("/auth/register/company", data);
  },
  // ============================================
  // LOGIN / LOGOUT
  // ============================================
  /**
   * Login with email and password
   */
  login: (data) => {
    return getApiClient().post("/auth/login", data);
  },
  /**
   * Logout and invalidate tokens
   */
  logout: (refreshToken) => {
    return getApiClient().post("/auth/logout", { refreshToken });
  },
  // ============================================
  // TOKEN
  // ============================================
  /**
   * Refresh access token
   */
  refreshToken: (data) => {
    return getApiClient().post("/auth/refresh", data);
  },
  // ============================================
  // EMAIL VERIFICATION
  // ============================================
  /**
   * Verify email with token
   */
  verifyEmail: (data) => {
    return getApiClient().post("/auth/verify-email", data);
  },
  /**
   * Resend verification email
   */
  resendVerification: (data) => {
    return getApiClient().post("/auth/resend-verification", data);
  },
  // ============================================
  // PASSWORD
  // ============================================
  /**
   * Request password reset email
   */
  forgotPassword: (data) => {
    return getApiClient().post("/auth/forgot-password", data);
  },
  /**
   * Reset password with token
   */
  resetPassword: (data) => {
    return getApiClient().post("/auth/reset-password", data);
  },
  /**
   * Change password (authenticated)
   */
  changePassword: (data) => {
    return getApiClient().patch("/auth/change-password", data);
  },
  // ============================================
  // ME
  // ============================================
  /**
   * Get current authenticated user
   */
  me: () => {
    return getApiClient().get("/auth/me");
  },
  /**
   * Login or register with Google
   */
  googleLogin: (data) => {
    return getApiClient().post("/auth/google", data);
  }
};

// src/endpoints/users.ts
var usersApi = {
  // ============================================
  // SEAFARERS - PUBLIC
  // ============================================
  /**
   * List public seafarer profiles
   */
  listSeafarers: (params) => {
    return getApiClient().get("/users/seafarers", { params });
  },
  /**
   * Get public seafarer profile by ID
   */
  getSeafarer: (id) => {
    return getApiClient().get(`/users/seafarers/${id}`);
  },
  // ============================================
  // SEAFARERS - AUTHENTICATED (ME)
  // ============================================
  /**
   * Get my seafarer profile
   */
  getMyProfile: () => {
    return getApiClient().get("/users/seafarers/me");
  },
  /**
   * Get my dashboard stats
   */
  getMyStats: () => {
    return getApiClient().get("/users/seafarers/me/stats");
  },
  /**
   * Get my profile view history
   */
  getProfileViews: (days) => {
    return getApiClient().get("/users/seafarers/me/profile-views", {
      params: { days }
    });
  },
  /**
   * Update my profile
   */
  updateMyProfile: (data) => {
    return getApiClient().patch("/users/seafarers/me", data);
  },
  /**
   * Update my preferences
   */
  updatePreferences: (data) => {
    return getApiClient().patch("/users/seafarers/me/preferences", data);
  },
  /**
   * Update profile visibility
   */
  updateVisibility: (data) => {
    return getApiClient().patch("/users/seafarers/me/visibility", data);
  },
  /**
   * Upload avatar
   */
  uploadAvatar: (file, onProgress) => {
    return getApiClient().upload(
      "/users/seafarers/me/avatar",
      file,
      "file",
      onProgress
    );
  },
  /**
   * Delete avatar
   */
  deleteAvatar: () => {
    return getApiClient().delete("/users/seafarers/me/avatar");
  },
  /**
   * Delete user account (Me)
   */
  deleteAccount: () => {
    return getApiClient().delete("/users/me");
  }
};

// src/endpoints/companies.ts
var companiesApi = {
  // ============================================
  // PUBLIC
  // ============================================
  /**
   * List companies
   */
  list: (params) => {
    return getApiClient().get("/companies", { params });
  },
  /**
   * Get company by slug
   */
  getBySlug: (slug) => {
    return getApiClient().get(`/companies/${slug}`);
  },
  /**
   * Get company jobs
   */
  getJobs: (slug, params) => {
    return getApiClient().get(`/companies/${slug}/jobs`, { params });
  },
  /**
   * Get company reviews
   */
  getReviews: (slug, params) => {
    return getApiClient().get(`/companies/${slug}/reviews`, { params });
  },
  // ============================================
  // AUTHENTICATED (ME)
  // ============================================
  /**
   * Get my company profile
   */
  getMyCompany: () => {
    return getApiClient().get("/companies/me");
  },
  /**
   * Get my company stats
   */
  getStats: () => {
    return getApiClient().get("/companies/me/stats");
  },
  /**
   * Get company analytics
   */
  getAnalytics: (period) => {
    return getApiClient().get("/companies/me/analytics", {
      params: { period }
    });
  },
  /**
   * Update my company
   */
  updateMyCompany: (data) => {
    return getApiClient().patch("/companies/me", data);
  },
  /**
   * Upload company logo
   */
  uploadLogo: (file, onProgress) => {
    return getApiClient().upload("/companies/me/logo", file, "file", onProgress);
  },
  /**
   * Upload company banner
   */
  uploadBanner: (file, onProgress) => {
    return getApiClient().upload("/companies/me/banner", file, "file", onProgress);
  },
  /**
   * Delete company logo
   */
  deleteLogo: () => {
    return getApiClient().delete("/companies/me/logo");
  },
  /**
   * Delete company banner
   */
  deleteBanner: () => {
    return getApiClient().delete("/companies/me/banner");
  }
};

// src/endpoints/jobs.ts
var jobsApi = {
  // ============================================
  // PUBLIC
  // ============================================
  /**
   * List active jobs
   */
  list: (params) => {
    return getApiClient().get("/jobs", { params });
  },
  /**
   * Get job by slug
   */
  getBySlug: (slug) => {
    return getApiClient().get(`/jobs/${slug}`);
  },
  /**
   * Get job by ID
   */
  getById: (id) => {
    return getApiClient().get(`/jobs/${id}`);
  },
  /**
   * Get similar jobs
   */
  getSimilar: (slug, limit) => {
    return getApiClient().get(`/jobs/${slug}/similar`, {
      params: { limit }
    });
  },
  // ============================================
  // SEAFARER
  // ============================================
  /**
   * Get recommended jobs for seafarer
   */
  getRecommended: (params) => {
    return getApiClient().get("/jobs/recommended", { params });
  },
  // ============================================
  // COMPANY
  // ============================================
  /**
   * Get my company's jobs
   * [ŞİRKET-H8] Department ve search filtresi desteği eklendi
   */
  getMyJobs: (params) => {
    return getApiClient().get("/jobs/me", { params });
  },
  /**
   * Create new job
   */
  create: (data) => {
    return getApiClient().post("/jobs", data);
  },
  /**
   * Update job
   */
  update: (id, data) => {
    return getApiClient().patch(`/jobs/${id}`, data);
  },
  /**
   * Update job status
   */
  updateStatus: (id, data) => {
    return getApiClient().patch(`/jobs/${id}/status`, data);
  },
  /**
   * Delete job
   */
  delete: (id) => {
    return getApiClient().delete(`/jobs/${id}`);
  },
  /**
   * Dope/Feature job to make it urgent
   */
  dope: (id) => {
    return getApiClient().post(`/jobs/${id}/dope`);
  },
  /**
   * Get job applications
   */
  getApplications: (id) => {
    return getApiClient().get(`/jobs/${id}/applications`);
  },
  /**
   * Get matching candidates for job
   */
  getMatches: (id, params) => {
    return getApiClient().get(`/jobs/${id}/matches`, { params });
  }
};

// src/endpoints/applications.ts
var applicationsApi = {
  // ============================================
  // SEAFARER
  // ============================================
  /**
   * Apply to a job
   * @throws {Error} Profile must be at least 80% complete
   */
  apply: (data) => {
    return getApiClient().post("/applications", data);
  },
  /**
   * Get my applications
   */
  getMyApplications: () => {
    return getApiClient().get("/applications/me");
  },
  /**
   * Withdraw application
   */
  withdraw: (id) => {
    return getApiClient().delete(`/applications/${id}`);
  },
  /**
   * [KULLANICI-H7] Check if user can apply to a job
   * Returns profile completeness and other eligibility info
   */
  checkCanApply: (jobId) => {
    return getApiClient().get(`/applications/can-apply/${jobId}`);
  },
  // ============================================
  // COMPANY
  // ============================================
  /**
   * Get all applications for company's jobs
   */
  getCompanyApplications: () => {
    return getApiClient().get("/applications/company");
  },
  /**
   * Get application by ID (includes basic seafarer info)
   */
  getById: (id) => {
    return getApiClient().get(`/applications/${id}`);
  },
  /**
   * [ŞİRKET-H1-H3] Get detailed seafarer profile for an application
   * Returns full profile with certificates, sea service records, documents
   */
  getSeafarerProfile: (applicationId) => {
    return getApiClient().get(`/applications/${applicationId}/seafarer-profile`);
  },
  /**
   * Update application status
   */
  updateStatus: (id, data) => {
    return getApiClient().patch(`/applications/${id}/status`, data);
  },
  /**
   * Update application notes
   */
  updateNotes: (id, data) => {
    return getApiClient().patch(`/applications/${id}/notes`, data);
  }
};

// src/endpoints/certificates.ts
var certificatesApi = {
  // ============================================
  // CERTIFICATE TYPES (PUBLIC)
  // ============================================
  /**
   * Get certificate types
   */
  getTypes: (category) => {
    return getApiClient().get("/certificates/types", {
      params: { category }
    });
  },
  /**
   * Get certificate type by ID
   */
  getTypeById: (id) => {
    return getApiClient().get(`/certificates/types/${id}`);
  },
  // ============================================
  // MY CERTIFICATES
  // ============================================
  /**
   * Get my certificates
   */
  getMyCertificates: () => {
    return getApiClient().get("/certificates/me");
  },
  /**
   * Get expiring certificates
   */
  getExpiring: (days) => {
    return getApiClient().get("/certificates/expiring", {
      params: { days }
    });
  },
  /**
   * Get missing certificates for career progression
   */
  getMissing: () => {
    return getApiClient().get("/certificates/missing");
  },
  /**
   * Create certificate
   */
  create: (data) => {
    return getApiClient().post("/certificates", data);
  },
  /**
   * Upload certificate document
   */
  uploadDocument: (id, file, onProgress) => {
    return getApiClient().upload(
      `/certificates/${id}/document`,
      file,
      "file",
      onProgress
    );
  },
  /**
   * Update certificate
   */
  update: (id, data) => {
    return getApiClient().patch(`/certificates/${id}`, data);
  },
  /**
   * Delete certificate
   */
  delete: (id) => {
    return getApiClient().delete(`/certificates/${id}`);
  }
};

// src/endpoints/sea-service.ts
var seaServiceApi = {
  /**
   * Get my sea service records
   */
  getMyRecords: () => {
    return getApiClient().get("/sea-service/me");
  },
  /**
   * Get sea service summary
   */
  getSummary: () => {
    return getApiClient().get("/sea-service/me/summary");
  },
  /**
   * Create sea service record
   */
  create: (data) => {
    return getApiClient().post("/sea-service", data);
  },
  /**
   * Upload document for sea service record
   */
  uploadDocument: (id, file, onProgress) => {
    return getApiClient().upload(
      `/sea-service/${id}/document`,
      file,
      "file",
      onProgress
    );
  },
  /**
   * Update sea service record
   */
  update: (id, data) => {
    return getApiClient().patch(`/sea-service/${id}`, data);
  },
  /**
   * Delete sea service record
   */
  delete: (id) => {
    return getApiClient().delete(`/sea-service/${id}`);
  }
};

// src/endpoints/documents.ts
var documentsApi = {
  /**
   * Get my documents
   */
  getMyDocuments: () => {
    return getApiClient().get("/documents/me");
  },
  /**
   * Get my folders
   */
  getFolders: () => {
    return getApiClient().get("/documents/folders");
  },
  /**
   * Get download URL for document
   */
  getDownloadUrl: (id) => {
    return getApiClient().get(`/documents/${id}/download`);
  },
  /**
   * Create document record
   */
  create: (data) => {
    return getApiClient().post("/documents", data);
  },
  /**
   * Create folder
   */
  createFolder: (data) => {
    return getApiClient().post("/documents/folders", data);
  },
  /**
   * Update document
   */
  update: (id, data) => {
    return getApiClient().patch(`/documents/${id}`, data);
  },
  /**
   * Move document to folder
   */
  move: (id, data) => {
    return getApiClient().patch(`/documents/${id}/move`, data);
  },
  /**
   * Update folder
   */
  updateFolder: (id, data) => {
    return getApiClient().patch(`/documents/folders/${id}`, data);
  },
  /**
   * Delete document
   */
  delete: (id) => {
    return getApiClient().delete(`/documents/${id}`);
  },
  /**
   * Delete folder
   */
  deleteFolder: (id) => {
    return getApiClient().delete(`/documents/folders/${id}`);
  }
};

// src/endpoints/favorites.ts
var favoritesApi = {
  // ============================================
  // SEAFARER - FAVORITE JOBS
  // ============================================
  /**
   * Get favorite jobs
   */
  getFavoriteJobs: () => {
    return getApiClient().get("/favorites/jobs");
  },
  /**
   * Add job to favorites
   */
  addFavoriteJob: (data) => {
    return getApiClient().post("/favorites/jobs", data);
  },
  /**
   * Update favorite job notes
   */
  updateFavoriteJob: (jobId, data) => {
    return getApiClient().patch(`/favorites/jobs/${jobId}`, data);
  },
  /**
   * Remove job from favorites
   */
  removeFavoriteJob: (jobId) => {
    return getApiClient().delete(`/favorites/jobs/${jobId}`);
  },
  // ============================================
  // COMPANY - FAVORITE CANDIDATES
  // ============================================
  /**
   * Get favorite candidates
   */
  getFavoriteCandidates: () => {
    return getApiClient().get("/favorites/candidates");
  },
  /**
   * Add candidate to favorites
   */
  addFavoriteCandidate: (data) => {
    return getApiClient().post("/favorites/candidates", data);
  },
  /**
   * Update favorite candidate notes/tags
   */
  updateFavoriteCandidate: (seafarerId, data) => {
    return getApiClient().patch(`/favorites/candidates/${seafarerId}`, data);
  },
  /**
   * Remove candidate from favorites
   */
  removeFavoriteCandidate: (seafarerId) => {
    return getApiClient().delete(`/favorites/candidates/${seafarerId}`);
  }
};

// src/endpoints/matching.ts
var matchingApi = {
  // ============================================
  // SEAFARER
  // ============================================
  /**
   * Get matching jobs for seafarer
   */
  getMatchingJobs: () => {
    return getApiClient().get("/matching/jobs");
  },
  /**
   * Get match score for specific job
   */
  getJobMatchScore: (jobId) => {
    return getApiClient().get(`/matching/jobs/${jobId}/score`);
  },
  // ============================================
  // COMPANY
  // ============================================
  /**
   * Get matching candidates for job (by query param)
   */
  getMatchingCandidates: (jobId, params) => {
    const searchParams = new URLSearchParams();
    searchParams.set("jobId", jobId);
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.minScore) searchParams.set("minScore", String(params.minScore));
    return getApiClient().get(`/matching/candidates?${searchParams.toString()}`);
  },
  /**
   * Get matching candidates for job (by path param)
   */
  getMatchingCandidatesByJob: (jobId) => {
    return getApiClient().get(`/matching/candidates/${jobId}`);
  },
  // ============================================
  // ADMIN
  // ============================================
  /**
   * Batch calculate match scores (admin only)
   */
  calculateBatch: (data) => {
    return getApiClient().post("/matching/calculate", data);
  }
};

// src/endpoints/reviews.ts
var reviewsApi = {
  // ============================================
  // PUBLIC
  // ============================================
  /**
   * Get company reviews
   */
  getCompanyReviews: (companyId) => {
    return getApiClient().get(`/reviews/company/${companyId}`);
  },
  /**
   * Get company review summary
   */
  getReviewSummary: (companyId) => {
    return getApiClient().get(`/reviews/company/${companyId}/summary`);
  },
  // ============================================
  // SEAFARER
  // ============================================
  /**
   * Get my reviews
   */
  getMyReviews: () => {
    return getApiClient().get("/reviews/me");
  },
  /**
   * Create review
   */
  create: (data) => {
    return getApiClient().post("/reviews", data);
  },
  /**
   * Update review
   */
  update: (id, data) => {
    return getApiClient().patch(`/reviews/${id}`, data);
  },
  /**
   * Delete review
   */
  delete: (id) => {
    return getApiClient().delete(`/reviews/${id}`);
  }
};

// src/endpoints/career.ts
var careerApi = {
  // ============================================
  // PUBLIC
  // ============================================
  /**
   * Get all ranks
   */
  getRanks: (department) => {
    return getApiClient().get("/career/ranks", {
      params: { department }
    });
  },
  /**
   * Get rank by ID
   */
  getRankById: (id) => {
    return getApiClient().get(`/career/ranks/${id}`);
  },
  /**
   * Get career paths
   */
  getCareerPaths: (department) => {
    return getApiClient().get("/career/paths", {
      params: { department }
    });
  },
  /**
   * Get career path by ID with steps
   */
  getCareerPathById: (id) => {
    return getApiClient().get(`/career/paths/${id}`);
  },
  // ============================================
  // SEAFARER
  // ============================================
  /**
   * Get my career progress
   */
  getMyProgress: () => {
    return getApiClient().get("/career/me/progress");
  },
  /**
   * Get next career steps and requirements
   */
  getNextSteps: () => {
    return getApiClient().get("/career/me/next-steps");
  },
  /**
   * Set career path
   */
  setCareerPath: (data) => {
    return getApiClient().post("/career/me/set-path", data);
  }
};

// src/endpoints/notifications.ts
var notificationsApi = {
  /**
   * Get my notifications
   */
  getNotifications: () => {
    return getApiClient().get("/notifications");
  },
  /**
   * Get unread notification count
   */
  getUnreadCount: () => {
    return getApiClient().get("/notifications/unread-count");
  },
  /**
   * Get notification settings
   */
  getSettings: () => {
    return getApiClient().get("/notifications/settings");
  },
  /**
   * Update notification settings
   */
  updateSettings: (data) => {
    return getApiClient().patch("/notifications/settings", data);
  },
  /**
   * Mark notification as read
   */
  markAsRead: (id) => {
    return getApiClient().patch(`/notifications/${id}/read`);
  },
  /**
   * Mark all notifications as read
   */
  markAllAsRead: () => {
    return getApiClient().patch("/notifications/read-all");
  },
  /**
   * Delete notification
   */
  delete: (id) => {
    return getApiClient().delete(`/notifications/${id}`);
  }
};

// src/endpoints/analytics.ts
var analyticsApi = {
  // ============================================
  // COMPANY
  // ============================================
  /**
   * Get company dashboard analytics
   */
  getCompanyDashboard: (period) => {
    return getApiClient().get("/analytics/company/dashboard", {
      params: period ? { period } : void 0
    });
  },
  /**
   * Get analytics for a specific job
   */
  getJobAnalytics: (jobId, period) => {
    return getApiClient().get(`/analytics/company/jobs/${jobId}`, {
      params: period ? { period } : void 0
    });
  },
  // ============================================
  // SEAFARER
  // ============================================
  /**
   * Get seafarer dashboard analytics
   */
  getSeafarerDashboard: () => {
    return getApiClient().get("/analytics/seafarer/dashboard");
  },
  // ============================================
  // MARKET TRENDS (PUBLIC)
  // ============================================
  /**
   * Get market trends
   */
  getMarketTrends: (period) => {
    return getApiClient().get("/analytics/trends", {
      params: period ? { period } : void 0
    });
  },
  /**
   * Get rank demand trends
   */
  getRankTrends: () => {
    return getApiClient().get("/analytics/trends/ranks");
  },
  /**
   * Get salary trends
   */
  getSalaryTrends: (department) => {
    return getApiClient().get("/analytics/trends/salaries", {
      params: department ? { department } : void 0
    });
  },
  /**
   * Get vessel type trends
   */
  getVesselTypeTrends: () => {
    return getApiClient().get("/analytics/trends/vessel-types");
  }
};

// src/endpoints/common.ts
var commonApi = {
  // ============================================
  // LOOKUPS
  // ============================================
  /**
   * Get all countries
   */
  getCountries: () => {
    return getApiClient().get("/common/countries");
  },
  /**
   * Get all nationalities
   */
  getNationalities: () => {
    return getApiClient().get("/common/nationalities");
  },
  /**
   * Get all departments
   */
  getDepartments: () => {
    return getApiClient().get("/common/departments");
  },
  /**
   * Get all vessel types
   */
  getVesselTypes: () => {
    return getApiClient().get("/common/vessel-types");
  },
  /**
   * Get all ranks
   */
  getRanks: (department) => {
    return getApiClient().get("/common/ranks", {
      params: department ? { department } : void 0
    });
  },
  /**
   * Get certificate types
   */
  getCertificateTypes: (category) => {
    return getApiClient().get("/common/certificate-types", {
      params: category ? { category } : void 0
    });
  },
  // ============================================
  // UPLOADS
  // ============================================
  /**
   * Upload an image (logo, avatar, banner, etc.)
   */
  uploadImage: (file, onProgress) => {
    return getApiClient().upload("/common/upload/image", file, "file", onProgress);
  },
  /**
   * Upload a document (PDF, DOC, etc.)
   */
  uploadDocument: (file, onProgress) => {
    return getApiClient().upload("/common/upload/document", file, "file", onProgress);
  }
};

// src/endpoints/admin.ts
var adminApi = {
  getDashboard: () => getApiClient().get("/admin/dashboard"),
  // [ADMİN-H3] Recent Activities
  getRecentActivities: (limit) => getApiClient().get("/admin/recent-activities", { params: { limit } }),
  // ============================================
  // USERS
  // ============================================
  getUsers: (params) => getApiClient().get("/admin/users", { params }),
  getUser: (id) => getApiClient().get(`/admin/users/${id}`),
  createUser: (data) => getApiClient().post("/admin/users", data),
  updateUser: (id, data) => getApiClient().put(`/admin/users/${id}`, data),
  updateUserStatus: (id, data) => getApiClient().patch(`/admin/users/${id}/status`, data),
  deleteUser: (id) => getApiClient().delete(`/admin/users/${id}`),
  // ============================================
  // COMPANIES
  // ============================================
  getCompanies: (params) => getApiClient().get("/admin/companies", { params }),
  getCompany: (id) => getApiClient().get(`/admin/companies/${id}`),
  createCompany: (data) => getApiClient().post("/admin/companies", data),
  updateCompany: (id, data) => getApiClient().put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => getApiClient().delete(`/admin/companies/${id}`),
  // ============================================
  // SEAFARERS
  // ============================================
  getSeafarers: (params) => getApiClient().get("/admin/seafarers", { params }),
  getSeafarer: (id) => getApiClient().get(`/admin/seafarers/${id}`),
  createSeafarer: (data) => getApiClient().post("/admin/seafarers", data),
  updateSeafarer: (id, data) => getApiClient().patch(`/admin/seafarers/${id}`, data),
  verifySeafarer: (id, isVerified) => getApiClient().patch(`/admin/seafarers/${id}/verify`, { isVerified }),
  // ============================================
  // JOBS - [ADMİN-H6] Department filter eklendi
  // ============================================
  getJobs: (params) => getApiClient().get("/admin/jobs", { params }),
  getJob: (id) => getApiClient().get(`/admin/jobs/${id}`),
  createJob: (data) => getApiClient().post("/admin/jobs", data),
  updateJob: (id, data) => getApiClient().put(`/admin/jobs/${id}`, data),
  deleteJob: (id) => getApiClient().delete(`/admin/jobs/${id}`),
  // ============================================
  // APPLICATIONS - [ADMİN-H5] Yeni endpoint
  // ============================================
  getApplications: (params) => getApiClient().get("/admin/applications", { params }),
  // ============================================
  // REVIEWS
  // ============================================
  getReviews: (status) => getApiClient().get("/admin/reviews", { params: { status } }),
  getPendingReviews: () => getApiClient().get("/admin/reviews/pending"),
  approveReview: (id) => getApiClient().patch(`/admin/reviews/${id}/approve`),
  rejectReview: (id) => getApiClient().patch(`/admin/reviews/${id}/reject`),
  // ============================================
  // CERTIFICATE TYPES
  // ============================================
  getCertificateTypes: () => getApiClient().get("/admin/certificate-types"),
  createCertificateType: (data) => getApiClient().post("/admin/certificate-types", data),
  updateCertificateType: (id, data) => getApiClient().patch(`/admin/certificate-types/${id}`, data),
  deleteCertificateType: (id) => getApiClient().delete(`/admin/certificate-types/${id}`),
  // ============================================
  // RANKS
  // ============================================
  getRanks: () => getApiClient().get("/admin/ranks"),
  createRank: (data) => getApiClient().post("/admin/ranks", data),
  updateRank: (id, data) => getApiClient().patch(`/admin/ranks/${id}`, data),
  deleteRank: (id) => getApiClient().delete(`/admin/ranks/${id}`),
  // ============================================
  // BADGES
  // ============================================
  awardBadge: (data) => getApiClient().post("/admin/badges/award", data),
  // ============================================
  // EMAIL TEMPLATES
  // ============================================
  getEmailTemplates: () => getApiClient().get("/admin/email-templates"),
  getEmailTemplate: (id) => getApiClient().get(`/admin/email-templates/${id}`),
  updateEmailTemplate: (id, data) => getApiClient().patch(`/admin/email-templates/${id}`, data),
  getEmailLogs: (params) => getApiClient().get("/admin/email-logs", { params }),
  getEmailLog: (id) => getApiClient().get(`/admin/email-logs/${id}`),
  previewEmailTemplate: (data) => getApiClient().post("/admin/email-templates/preview", data),
  // ============================================
  // ADMIN PROFILE
  // ============================================
  getProfile: () => getApiClient().get("/admin/profile"),
  updateProfile: (data) => getApiClient().patch("/admin/profile", data),
  // ============================================
  // VERIFICATIONS
  // ============================================
  getVerifications: (params) => getApiClient().get("/admin/verifications", { params }),
  getVerification: (id) => getApiClient().get(`/admin/verifications/${id}`),
  approveVerification: (id) => getApiClient().patch(`/admin/verifications/${id}/approve`),
  rejectVerification: (id, note) => getApiClient().patch(`/admin/verifications/${id}/reject`, { note })
};

// src/endpoints/payments.ts
var paymentsApi = {
  /**
   * Initiate payment with PayTR and get iframe session parameters
   */
  initiate: (data) => {
    return getApiClient().post("/payments/initiate", data);
  }
};

// src/endpoints/advertisements.ts
var advertisementsApi = {
  /**
   * Fetch the current active advertisement banner
   */
  getActive: () => {
    return getApiClient().get("/advertisements/active");
  },
  /**
   * List all advertisements (Admin only)
   */
  getAll: () => {
    return getApiClient().get("/advertisements");
  },
  /**
   * Create a new advertisement (Admin only)
   */
  create: (formData) => {
    return getApiClient().post("/advertisements", formData);
  },
  /**
   * Update advertisement metadata/image (Admin only)
   */
  update: (id, formData) => {
    return getApiClient().patch(`/advertisements/${id}`, formData);
  },
  /**
   * Delete an advertisement (Admin only)
   */
  delete: (id) => {
    return getApiClient().delete(`/advertisements/${id}`);
  }
};

// src/endpoints/support.ts
var supportApi = {
  /**
   * Send a support request
   * Destek talebi gönder - email olarak iletilir
   */
  sendRequest: (data) => {
    return getApiClient().post("/support/send", data);
  }
};

export { ApiClient, adminApi, advertisementsApi, analyticsApi, applicationsApi, authApi, careerApi, certificatesApi, commonApi, companiesApi, createApiClient, documentsApi, favoritesApi, getApiClient, jobsApi, matchingApi, notificationsApi, paymentsApi, reviewsApi, seaServiceApi, supportApi, usersApi };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map